import type { Alarm, AlarmCreate, AlarmUpdate, ApiError } from '@obv/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { keys } from './keys';

// Callers mint the Idempotency-Key once per user action (newMutationKey), so retries replay safely.

/** This display's alarms. Persisted with the rest of the cache so they still ring offline. */
export function useAlarms(enabled = true) {
  return useQuery({
    queryKey: keys.alarms.list(),
    queryFn: () => api.alarms.list().then((r) => r.items),
    enabled,
    staleTime: 5 * 60_000,
    cacheTime: Infinity,
  });
}

export function useSaveAlarm() {
  const qc = useQueryClient();
  return useMutation<Alarm, ApiError, { id?: string; body: AlarmCreate | AlarmUpdate; key: string }>({
    mutationFn: (v) => (v.id ? api.alarms.update(v.id, v.body as AlarmUpdate, v.key) : api.alarms.create(v.body as AlarmCreate, v.key)),
    onMutate: (v) => {
      if (!v.id) return;
      qc.setQueryData<Alarm[]>(keys.alarms.list(), (old) => (old ? old.map((a) => (a.id === v.id ? ({ ...a, ...v.body } as Alarm) : a)) : old));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.alarms.list() }),
  });
}

export function useDeleteAlarm() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { id: string; key: string }>({
    mutationFn: (v) => api.alarms.remove(v.id, v.key),
    onMutate: ({ id }) => {
      qc.setQueryData<Alarm[]>(keys.alarms.list(), (old) => (old ? old.filter((a) => a.id !== id) : old));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.alarms.list() }),
  });
}

/** Tells the server a ring was stopped; it switches one-time alarms off. */
export function useDismissAlarm() {
  const qc = useQueryClient();
  return useMutation<Alarm, ApiError, { id: string; key: string }>({
    mutationFn: (v) => api.alarms.dismiss(v.id, v.key),
    onSuccess: (a) => qc.setQueryData<Alarm[]>(keys.alarms.list(), (old) => (old ? old.map((x) => (x.id === a.id ? a : x)) : old)),
  });
}
